import React, { useState, useEffect } from 'react'
import { Timestamp } from '../components/ui/Timestamp'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { TaskReminderSection } from '../components/reminders/TaskReminderSection'
import { useProvideFeedbackContext } from '../components/feedback/FeedbackContext'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { useToast } from '../components/ui/Toast'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { ArrowLeft, Trash2, CheckCircle2, Clock, AlertCircle, RefreshCw, Calendar, Repeat, Play, ListChecks } from 'lucide-react'
import { ActionMenu } from '../components/ui/ActionMenu'
import { canStart } from '../utils/taskSections'
import { TaskSteps } from '../components/tasks/TaskSteps'
import { TaskHistory } from '../components/tasks/TaskHistory'
import { TaskProgress } from '../components/tasks/TaskProgress'
import { OccurrencesSection } from '../components/occurrences/OccurrencesSection'
import { LifeAreaPicker } from '../components/tasks/LifeAreaPicker'
import { WhenEditor, DurationEditor, FitEditor, ParentEditor, PriorityEditor, AddDetailsRow } from '../components/tasks/inline/TaskInlineEditors'
import { TitleEditor, WhyEditor, DoneMeansEditor, FirstStepEditor, StakesEditor, WhereEditor, RepeatsEditor } from '../components/tasks/inline/TaskWordEditors'
import { AlignLeft } from 'lucide-react'
import { TaskService, type Task } from '../services/taskService'
import { connectionForCompletedItem } from '../services/hortonGuidanceService'
import { PlanItMenu } from '../components/tasks/PlanItMenu'
import DreamGraphTabs from '../components/hierarchy/DreamGraphTabs'
import BlockersSection from '../components/blockers/BlockersSection'
import { TaskGapsCard } from '../components/tasks/TaskGapsCard'
import { PRIORITY_LABEL, STATUS_LABEL, WINDOW_LABEL, dueLine, describeTaskCleanup, durationText, fitWords, recurrenceWords, stakesWords, whenWords } from '../utils/taskForm'

// Section headings share one voice (owner 2026-09-05: "Plan" and "Not on the
// calendar" read as the same font) — bold, tracked, with a brand icon.
const SECTION_HEADING = 'flex items-center gap-1.5 text-app-meta font-bold uppercase tracking-widest text-secondary'

export const TaskDetail: React.FC = () => {
 const { id } = useParams<{ id: string }>()
 const navigate = useNavigate()
 const [task, setTask] = useState<Task | null>(null)
 const [loading, setLoading] = useState(true)
 const [error, setError] = useState<string | null>(null)
 // UX-10: parent title lookup maps (type:id -> title)
 // UX-8: in-app delete confirmation instead of window.confirm()
 const [showDeleteModal, setShowDeleteModal] = useState(false)
 // About: the description is edited in place (owner 2026-09-05, point 3).
 const [editingDesc, setEditingDesc] = useState(false)
 const [historyTick, setHistoryTick] = useState(0)
 // "Show steps" (owner 2026-09-05): only when there are steps; it opens the
 // Steps section and brings it into view. Counter so repeat presses work.
 const [stepsOpenRequest, setStepsOpenRequest] = useState(0)
 const [descDraft, setDescDraft] = useState('')

 // AI help state
 const [aiHelpLoading, setAiHelpLoading] = useState(false)
 const [aiHelpError, setAiHelpError] = useState<string | null>(null)
 // What people look at but do not do is a signal (contract §4): one event per open.
 useEffect(() => {
  if (!id) return
  void fetch('/api/orchestrator/engagement-event', {
   method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
   body: JSON.stringify({ event_type: 'task_viewed', context: 'task_detail', data: { task_id: Number(id) } }),
  }).catch(() => undefined)
 }, [id])

 const toast = useToast()

 useProvideFeedbackContext({ pageType: 'task_detail', entityType: 'task', entityId: id, extra: { title: task?.title, status: task?.status } })

 const taskService = new TaskService()

 useEffect(() => {
 if (id) {
 loadTask()
 }
 }, [id])

 const loadTask = async () => {
 if (!id) return

 try {
 setLoading(true)
 setError(null)
 const item = await taskService.getTask(parseInt(id))
 setTask(item)
 // §P1-7: the parent comes with the item (task.parent); no list fetches.
 } catch (err) {
 console.error('Error loading task:', err)
 setError(err instanceof Error ? err.message : 'Failed to load task')
 } finally {
 setLoading(false)
 }
 }

 const getPriorityColor = (priority: string) => {
 switch (priority) {
 case 'urgent':
 case 'high':
 return 'bg-bad/20 text-bad'
 case 'medium':
 return 'bg-warn/20 text-warn'
 case 'low':
 return 'bg-surface-alt text-secondary'
 default:
 return 'bg-surface-alt text-secondary'
 }
 }

 const handleBreakdown = async () => {
 if (!task) return
 try {
 setAiHelpLoading(true)
 setAiHelpError(null)
 await taskService.breakdownTask(task.id)
 await loadTask()
} catch (err) {
console.error('Error generating breakdown:', err)
 setAiHelpError(err instanceof Error ? err.message : 'Failed to generate breakdown')
 } finally {
 setAiHelpLoading(false)
 }
 }



 // Undo on the Done toast, the same forgiveness the list gives (one tap
 // finishes it, the way back is right there).
 const reopen = async () => {
 if (!task) return
 try {
 await taskService.updateTask(task.id, { status: 'pending' })
 await loadTask()
 } catch (err) {
 toast.error('Could not reopen it', err instanceof Error ? err.message : 'Please try again.')
 }
 }

 // UX-8: delete via shared ConfirmationModal
 const handleDeleteConfirm = async () => {
 if (!task) return
 try {
 const result = await taskService.deleteTask(task.id)
 setShowDeleteModal(false)
 toast.toast({ variant: 'success', title: 'Task deleted', description: describeTaskCleanup(result.cleanup) || undefined })
 navigate('/tasks')
 } catch (err) {
 console.error('Error deleting task:', err)
 setShowDeleteModal(false)
 toast.error('Could not delete it', err instanceof Error ? err.message : 'Please try again.')
 }
 }

 if (loading) {
 // UX-15: unified loading variant (matches Dream/Goal/Aspiration detail)
 return (
 <div className="flex items-center justify-center h-64">
 <RefreshCw className="w-6 h-6 animate-spin text-brand" />
 <span className="ml-3 text-secondary">Loading task...</span>
 </div>
 )
 }

 if (error || !task) {
 return (
 <div className="space-y-4">
 <Button variant="outline" onClick={() => navigate('/tasks')} className="mb-4">
 <ArrowLeft className="w-4 h-4 mr-2" /> Back to Tasks
 </Button>
 <Card className="border-bad/30">
 <CardContent className="pt-6">
 <div className="text-center py-8">
 <AlertCircle className="w-12 h-12 text-bad mx-auto mb-4" />
 <h2 className="text-xl font-semibold text-primary mb-2">{error || 'Task Not Found'}</h2>
 <Button onClick={() => navigate('/tasks')}>Go to Tasks</Button>
 </div>
 </CardContent>
 </Card>
 </div>
 )
 }

 // Laptop pass (2026-09-05, owner: "they should not have to gaze at the
 // entire screen to understand what that page means"). One frame: a
 // breadcrumb, the title with a one-line summary beside it, the verbs, then
 // the task's own words on the left and a rail on the right that holds the
 // readiness prompt, the plan and reminders. Empty things cost one line, not
 // a card. Type/category/timestamps are chrome and live in a footer line.
 const parentRef = (() => {
 const p = task.parent
 const fallback = task.parent_goal_id ? { type: 'goal', id: task.parent_goal_id }
 : task.parent_skill_id ? { type: 'skill', id: task.parent_skill_id }
 : task.parent_aspiration_id ? { type: 'aspiration', id: task.parent_aspiration_id }
 : task.parent_dream_id ? { type: 'dream', id: task.parent_dream_id } : null
 const ref = p || fallback
 if (!ref) return null
 // A parent the server could not name reads as its kind, never as an id.
 return { title: p?.title || (ref.type === 'goal' ? 'Goal' : ref.type === 'skill' ? 'Skill' : 'Where it belongs'), href: ref.type === 'aspiration' ? '/goals' : `/${ref.type}s/${ref.id}` }
 })()
 const isDone = task.status === 'completed'
 const isLapsed = task.status === 'lapsed'
 const isOpen = !isDone && !isLapsed && task.status !== 'cancelled'
 const statusDot = isDone ? 'bg-ok' : isLapsed ? 'bg-bad' : task.status === 'in_progress' ? 'bg-brand' : task.status === 'on_hold' ? 'bg-warn' : 'bg-soft'
 // The complete-by, as an instant, and whether it has passed while the task is still open.
 const deadlineAt = task.due_date ? new Date(task.due_date.length <= 10 ? `${task.due_date}T${task.due_time || '23:59'}:00` : task.due_date) : null
 const deadlinePassed = Boolean(isOpen && deadlineAt && !Number.isNaN(deadlineAt.getTime()) && deadlineAt.getTime() < Date.now())
 const startedAt = task.status === 'in_progress' ? task.started_at : null
 const finishedAt = isDone ? (task.finished_at || task.completion_date) : null
 const stepCount = (task.steps || []).length + (task.child_action_items || []).filter((a) => a.status !== 'cancelled').length
 const est = durationText(task.estimated_duration)
 const took = durationText(task.actual_duration)
 const whenText = task.due_date ? dueLine(task) : task.target_window ? (WINDOW_LABEL[task.target_window] || task.target_window) : null
 // Same words as the Plan block: "Complete by" for a hard date, "Due" otherwise.
 const whenShort = whenWords(task)
 // What has accrued, said once: sessions when counted, else a percentage.
 // What is NOT set is not said here (North Star rule 2, 2026-09-12): the Add
 // row and the gaps card already carry it, and the line used to make three.
 const progressWords = (task.sessions_target ?? 0) > 0
 ? `${task.sessions_done || 0} of ${task.sessions_target} sessions`
 : (task.progress ?? 0) > 0 && !isDone ? `${Math.min(100, Math.round(task.progress || 0))}% done` : null
 const summaryParts = [
 progressWords,
 task.user_tier === 'backlog' && isOpen ? 'backlog' : null,
 task.is_recurring ? 'repeats' : null,
 ].filter(Boolean) as string[]
 type Fact = { key: string; label: string; value: React.ReactNode; testId?: string }
 // North Star rule 3 (2026-09-12): one dominant action per screen. The page's
 // one filled button is the next move — Start until it is started, Done once
 // it is — unless the passed-deadline review is showing, in which case its
 // "It got done" is the one and the verbs row stays quiet.
 const startLeads = canStart(task) && !deadlinePassed
 const doneLeads = isOpen && task.status === 'in_progress' && !deadlinePassed
 const menuItems = [
 { label: 'Delete', icon: <Trash2 className="h-4 w-4" />, onSelect: () => setShowDeleteModal(true), danger: true, testId: 'task-delete' },
 ]
 const bodyFacts: Fact[] = []
 if (task.motivation) bodyFacts.push({ key: 'why', label: 'Why it matters', value: isOpen ? <WhyEditor task={task} onChanged={setTask} /> : task.motivation })
 if (task.acceptance_criteria) bodyFacts.push({ key: 'done', label: 'Done means', value: isOpen ? <DoneMeansEditor task={task} onChanged={setTask} /> : task.acceptance_criteria })
 if (task.minimum_starter_step) bodyFacts.push({ key: 'first', label: 'First step', value: isOpen ? <FirstStepEditor task={task} onChanged={setTask} /> : task.minimum_starter_step })
 if (stakesWords(task)) bodyFacts.push({ key: 'stakes', label: 'If the time passes', value: isOpen ? <StakesEditor task={task} onChanged={setTask} /> : stakesWords(task) })
 if (fitWords(task)) bodyFacts.push({ key: 'fits', label: 'Fits', value: isOpen ? <FitEditor task={task} onChanged={setTask} /> : fitWords(task) })
 if (task.location || task.place_id) bodyFacts.push({ key: 'where', label: 'Where', value: isOpen ? <WhereEditor task={task} onChanged={setTask} /> : (task.location || 'A saved place') })

 return (
 <div className="space-y-4">
 <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-app-meta text-muted min-w-0" data-testid="task-breadcrumb">
 <Link to="/tasks" className="inline-flex items-center gap-1 hover:text-brand shrink-0">
 <ArrowLeft className="h-3.5 w-3.5" /> Tasks
 </Link>
 {parentRef && (
 <>
 <span aria-hidden="true">/</span>
 <Link to={parentRef.href} className="truncate hover:text-brand" data-testid="task-parent-link">{parentRef.title}</Link>
 </>
 )}
 {isOpen && parentRef && <ParentEditor task={task} onChanged={setTask} variant="icon" />}
 </nav>

 <Card>
 <CardHeader className="p-4 sm:p-6 pb-3 sm:pb-4">
 <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
 <CardTitle className={`text-xl sm:text-2xl leading-tight ${isDone ? 'text-muted' : 'text-primary'}`}>
 {isOpen ? <TitleEditor task={task} onChanged={setTask} trigger={task.title} /> : task.title}
 </CardTitle>
 <div className="inline-flex flex-wrap items-center gap-x-1.5 text-app-meta text-secondary" data-testid="task-summary-line">
 <span className="inline-flex items-center gap-1.5">
 <span className={`h-2 w-2 rounded-full ${statusDot}`} aria-hidden="true" />
 {STATUS_LABEL[task.status] || task.status}
 </span>
 {startedAt && <span>· started <Timestamp value={startedAt} mode="datetime" /></span>}
 <span>· <LifeAreaPicker task={task} onChanged={setTask} editable={isOpen} /></span>
 {whenShort && (isOpen ? <span>· <WhenEditor task={task} onChanged={setTask} trigger={whenShort} /></span> : <span>· {whenShort}</span>)}
 {finishedAt && <span>· finished <Timestamp value={finishedAt} mode="datetime" />{task.deadline_outcome === 'late' ? ', a little after the time' : task.deadline_outcome === 'met' ? ', in time' : ''}</span>}
 {isLapsed && task.lapsed_at && <span>· let go <Timestamp value={task.lapsed_at} mode="datetime" /></span>}
 {summaryParts.map((part) => <span key={part}>· {part}</span>)}
 {(task.priority === 'high' || task.priority === 'urgent') && (
 isOpen
 ? <PriorityEditor task={task} onChanged={setTask} className="ml-1" trigger={<span className={`px-1.5 py-0.5 rounded text-app-micro font-medium ${getPriorityColor(task.priority)}`}>{PRIORITY_LABEL[task.priority] || task.priority}</span>} />
 : <span className={`ml-1 px-1.5 py-0.5 rounded text-app-micro font-medium ${getPriorityColor(task.priority)}`}>{PRIORITY_LABEL[task.priority] || task.priority}</span>
 )}
 </div>
 </div>

 <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="task-actions">
 {canStart(task) && (
 <Button
 variant={startLeads ? 'default' : 'outline'}
 size="xs"
 className={startLeads ? '' : 'hover:bg-brand-subtle'}
 onClick={async () => {
 try {
 await taskService.updateTask(task.id, { status: 'in_progress' })
 await loadTask()
 } catch (err) {
 toast.error('Could not start it', err instanceof Error ? err.message : 'Please try again.')
 }
 }}
 data-testid="task-start"
 >
 <Play className="h-4 w-4 mr-2" />
 Start
 </Button>
 )}
 {isOpen && (
 <Button
 variant={doneLeads ? 'default' : 'outline'}
 size="xs"
 className={doneLeads ? '' : 'hover:bg-ok/10'}
 data-testid="task-done"
 onClick={async () => {
 try {
 const result = await taskService.markTaskComplete(task.id)
 await loadTask()
 void connectionForCompletedItem({ type: 'task', title: task.title }).then((cn) => {
 if (!cn) return
 toast.toast({
 variant: 'info',
 title: `Completing this also helps "${cn.thread.title}"`,
 description: cn.why,
 action: { label: 'See it', onClick: () => navigate('/horton-inbox') },
 })
 })
 if (result.journey_step_unlocked) {
 const unlocked = result.journey_step_unlocked
 toast.toast({
 variant: 'info',
 title: 'Next step unlocked',
 description: unlocked.title,
 action: {
 label: 'Schedule Now',
 onClick: () => navigate('/review-schedule', {
 state: { items: [{ id: unlocked.id, type: 'task' as const, title: unlocked.title, priority: 'medium', estimated_duration: unlocked.estimated_duration || 30 }] }
 })
 }
 })
 } else {
 // The same toast as the list: a verb, the title, and Undo.
 toast.toast({ variant: 'success', title: 'Done', description: task.title, action: { label: 'Undo', onClick: () => void reopen() } })
 }
 } catch (err) {
 console.error('Error marking as complete:', err)
 toast.error('Could not finish the task', err instanceof Error ? err.message : 'Please try again.')
 }
 }}
 >
 <CheckCircle2 className="h-4 w-4 mr-2" />
 Done
 </Button>
 )}
 {(isDone || isLapsed) && (
 <Button
 variant="outline"
 size="xs"
 className="hover:bg-brand-subtle"
 onClick={async () => {
 try {
 await taskService.updateTask(task.id, { status: 'pending' })
 await loadTask()
 toast.success('Reopened')
 } catch (err) {
 toast.error('Could not reopen it', err instanceof Error ? err.message : 'Please try again.')
 }
 }}
 data-testid="task-reopen"
 >
 <RefreshCw className="h-4 w-4 mr-2" />
 Reopen
 </Button>
 )}
 {stepCount > 0 && (
 <Button variant="outline" size="xs" className="hover:bg-brand-subtle" onClick={() => setStepsOpenRequest((n) => n + 1)} data-testid="task-show-steps">
 <ListChecks className="h-4 w-4 sm:mr-2" />
 <span className="hidden sm:inline">Show steps</span>
 </Button>
 )}
 <ActionMenu label={`More for ${task.title}`} items={menuItems} testId="task-more" />
 </div>
 {deadlinePassed && (
 <div className="mt-3 rounded-lg border border-warn/40 bg-warn/5 p-3" data-testid="task-deadline-review">
 <p className="text-sm text-primary">The time you set for this has passed{deadlineAt ? <> (<Timestamp value={deadlineAt.toISOString()} mode="datetime" />)</> : null}. How did it go?</p>
 <div className="mt-2 flex flex-wrap items-center gap-2">
 <Button size="xs" onClick={async () => { try { await taskService.markTaskComplete(task.id); await loadTask(); toast.success('Done. Nice one.') } catch (err) { toast.error('Could not mark it done', err instanceof Error ? err.message : 'Please try again.') } }} data-testid="task-deadline-done">It got done</Button>
 <WhenEditor task={task} onChanged={setTask} preferDate variant="chip" trigger={<>It still matters · pick a new time</>} />
 <Button size="xs" variant="outline" className="hover:bg-brand-subtle" onClick={async () => { try { await taskService.updateTask(task.id, { status: 'lapsed' }); await loadTask(); toast.toast({ variant: 'info', title: 'Let go for now', description: 'It stays in your history, and the reminders are off. You can pick it up again any time.' }) } catch (err) { toast.error('Could not close it', err instanceof Error ? err.message : 'Please try again.') } }} data-testid="task-deadline-lapse">Let it go for now</Button>
 </div>
 </div>
 )}
 {aiHelpError && (
 <div className="mt-2 flex items-center gap-2 text-sm text-bad">
 <AlertCircle className="h-4 w-4 flex-shrink-0" />
 <span>{aiHelpError}</span>
 </div>
 )}
 {aiHelpLoading && <p className="mt-2 text-app-meta text-muted">Working on it…</p>}
 </CardHeader>

 <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
 <div className="grid gap-6 border-t border-soft pt-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
 {/* The task in its own words. */}
 <div className="min-w-0 space-y-5" data-testid="task-body">
 <section data-testid="task-about">
 <div className="flex items-center justify-between gap-2">
 <h3 className={SECTION_HEADING}><AlignLeft className="h-3.5 w-3.5 text-brand" /> About</h3>
 {isOpen && !editingDesc && (
 <button type="button" onClick={() => { setDescDraft(task.description || ''); setEditingDesc(true) }} className="text-app-meta font-medium text-brand hover:underline" data-testid="task-description-edit">
 {task.description ? 'Edit' : 'Add a description'}
 </button>
 )}
 </div>
 {editingDesc ? (
 <div className="mt-2 space-y-2">
 <textarea
 value={descDraft}
 onChange={(e) => setDescDraft(e.target.value)}
 rows={4}
 autoFocus
 className="w-full rounded-md border border-soft bg-surface p-2 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand/30"
 aria-label="Description"
 data-testid="task-description-input"
 />
 <div className="flex gap-2">
 <Button size="sm" onClick={async () => {
 try {
 await taskService.updateTask(task.id, { description: descDraft.trim() })
 setEditingDesc(false)
 await loadTask()
 } catch (err) {
 toast.error('Could not save the description', err instanceof Error ? err.message : 'Please try again.')
 }
 }} data-testid="task-description-save">Save</Button>
 <Button size="sm" variant="ghost" onClick={() => setEditingDesc(false)}>Cancel</Button>
 </div>
 </div>
 ) : task.description ? (
 <p className="mt-1.5 whitespace-pre-line text-sm sm:text-base text-primary">{task.description}</p>
 ) : (
 <p className="mt-1.5 text-sm text-muted">Nothing written down yet. A line or two helps when you come back to it.</p>
 )}
 {isOpen && <div className="mt-3"><AddDetailsRow task={task} onChanged={setTask} /></div>}
 {bodyFacts.length > 0 && (
 <dl className="mt-3 text-sm" data-testid="task-facts">
 {bodyFacts.map((f) => (
 <div key={f.key} className="flex gap-3 py-1" data-testid={f.testId}>
 <dt className="w-28 shrink-0 pt-0.5 text-app-meta text-muted">{f.label}</dt>
 <dd className="min-w-0 break-words text-primary">{f.value}</dd>
 </div>
 ))}
 </dl>
 )}
 {task.is_recurring && (
 <p className="mt-2 flex flex-wrap items-center gap-x-2 text-sm text-secondary" data-testid="task-recurrence">
 {isOpen ? <RepeatsEditor task={task} onChanged={setTask} /> : <><Repeat className="h-3.5 w-3.5 text-muted" /><span>{recurrenceWords(task)}</span></>}
 </p>
 )}
 </section>

 <div className="border-t border-soft pt-4">
 <TaskSteps task={task} onChanged={setTask} onBreakdown={handleBreakdown} breakdownBusy={aiHelpLoading} openRequest={stepsOpenRequest} />
 </div>

 {id && !Number.isNaN(parseInt(id)) && (
 <BlockersSection itemType="task" itemId={parseInt(id)} className="border-t border-soft pt-4" />
 )}

 {task.is_recurring && (
 <div className="border-t border-soft pt-4">
 <OccurrencesSection itemType="task" itemId={task.id} refreshKey={`${task.updated_at}-${historyTick}`} headingClass={SECTION_HEADING} />
 </div>
 )}

 <div className="border-t border-soft pt-4">
 <TaskProgress task={task} onChanged={setTask} onLogged={() => setHistoryTick((n) => n + 1)} headingClass={SECTION_HEADING} />
 </div>

 <div className="border-t border-soft pt-4">
 <TaskHistory taskId={task.id} refreshKey={`${task.updated_at}-${task.status}-${(task.steps || []).length}-${task.progress}-${historyTick}`} canNote={isOpen} createdAt={task.created_at} updatedAt={task.updated_at} />
 </div>
 </div>

 {/* The rail: readiness, plan, reminders. */}
 <aside className="min-w-0 space-y-4 rounded-xl bg-surface-alt p-4" data-testid="task-rail">
 {isOpen && <TaskGapsCard task={task} onChanged={setTask} embedded />}
 <div className={isOpen ? 'border-t border-soft pt-4' : ''} data-testid="task-plan">
 <h3 className={SECTION_HEADING}><Calendar className="h-3.5 w-3.5 text-brand" /> Plan</h3>
 <div className="mt-1.5 space-y-1 text-sm text-primary">
 {whenText ? (
 <div data-testid="task-due-line">{isOpen ? <WhenEditor task={task} onChanged={setTask} trigger={whenText} /> : whenText}</div>
 ) : (
 <div className="text-secondary">{task.status === 'in_progress' && task.started_at ? <>Underway since <Timestamp value={task.started_at} mode="datetime" /></> : isOpen ? <WhenEditor task={task} onChanged={setTask} trigger="Not on the calendar" /> : 'Was never scheduled'}</div>
 )}
 {(est || (isDone && took)) && (
 <div className="flex items-center gap-1.5">
 <Clock className="h-3.5 w-3.5 shrink-0 text-muted" />
 <span>{isDone && took ? `Took ${took}${est ? ` (planned ${est})` : ''}` : isOpen ? <DurationEditor task={task} onChanged={setTask} trigger={`Takes ${est}`} /> : `Takes ${est}`}</span>
 </div>
 )}
 {isOpen && (
 <div className="pt-1"><PlanItMenu task={task} onChanged={loadTask} asLink label={whenText ? 'Change the plan' : 'Plan it'} /></div>
 )}
 </div>
 </div>
 {isOpen && (
 <TaskReminderSection
 taskId={Number(task.id)}
 taskTitle={task.title}
 deadline={task.due_date || null}
 hardDeadline={task.deadline_type === 'hard'}
 embedded
 />
 )}
 </aside>
 </div>

 </CardContent>
 </Card>

 {/* Unified life-map graph (steps live here). */}
 {task?.id && <DreamGraphTabs nodeId={task.id} nodeType="task" />}

 {/* UX-8: in-app delete confirmation (shared ConfirmationModal) */}
 <ConfirmationModal
 isOpen={showDeleteModal}
 onClose={() => setShowDeleteModal(false)}
 onConfirm={handleDeleteConfirm}
 title="Delete this task?"
 message={`“${task.title}” will be removed, along with anything scheduled or set to remind you about it.`}
 variant="error"
 confirmText="Delete"
 />
 </div>
 )
}

